/** Class to represent a table column */
class TableColumn {
  constructor(
    header,
    displayValue,
    sortValue,
    sortType,
    isHtml,
    htmlCallback,
    withActionBtn,
    actionList,
    cell,
    columnStyle,
    shouldRender
  ) {
    this.header = header.toUpperCase();
    this.displayValue = displayValue;

    this.sortValue = sortValue;
    this.sortType = sortType;

    this.isHtml = isHtml || false;
    this.htmlCallback = htmlCallback || null;

    this.withActionBtn = withActionBtn || false;
    this.actionList = actionList || [];
    this.cell = cell || false;

    this.columnStyle = columnStyle || null;
    this.shouldRender = shouldRender || (() => true);
  }
}

/** A Builder Factory class for Table columns */
export class TableColumnBuilder {

  /**
   * The basic fields needed for the table
   * @param {String} header - The table column name 
   * @param {String} displayValue  - The variable in the row object to display in this column, the variable must be a string  
   * @returns {TableColumnBuilder} The TableBuilder itself so it can be chained with other methods
   */
  addHeaderAndAccessor(header, displayValue) {
    this.header = header;
    this.displayValue = displayValue;
    return this;
  }

  /**
   * A function to add sorting to the table
   * @param {String} sortValue The variable in the row object used to sort the table, can be different from displayValue but must be a valid sortType
   * @param {sortTypes} sortType A sortType used to determine which type of sorting is applied on sortValue 
   * @returns {TableColumnBuilder} The TableBuilder itself so it can be chained with other methods
   */
  addSorting(sortValue, sortType) {
    this.sortValue = sortValue;
    this.sortType = sortType;
    return this;
  }

  /**
   * A function which renders html in the column cell instead of the displayValue
   * @param {Function} htmlCallback A function which returns some html to display in the row cell of this column 
   * @returns {TableColumnBuilder} The TableBuilder itself so it can be chained with other methods
   */
  addHtml(htmlCallback) {
    this.isHtml = true;
    this.htmlCallback = htmlCallback;
    return this;
  }

  /**
   * A function to determine if this column should be rendered 
   * @param {Boolean} evaluateFunction A Boolean 
   * @returns {TableColumnBuilder} The TableBuilder itself so it can be chained with other methods
   */
  addRenderCondition(evaluateFunction) {
    this.shouldRender = evaluateFunction;
    return this;
  }

  /**
   * A function to add a style to determine the style of this column such as right aligning it
   * @param {String} css A CSS string
   * @returns {TableColumnBuilder} The TableBuilder itself so it can be chained with other methods
   */
  addColumnStyle(css) {
    this.columnStyle = css;
    return this;
  }

  /**
   * A function to add a context menu to this table, passing actionList is not necessary, can be passed as tableActions to BaseTable
   * @param {Array.<ActionButton>?} actionList a list of action buttons for this table's context menu
   * @returns {TableColumnBuilder} The TableBuilder itself so it can be chained with other methods
   */
  addActions(actionList) {
    this.withActionBtn = true;
    this.actionList = actionList;
    return this;
  }

  /**
   * The final build method to initialize a table column
   * @returns {TableColumn} Returns a initialized TableColumn
   */
  build() {
    return new TableColumn(
        this.header,
        this.displayValue,
        this.sortValue,
        this.sortType,
        this.isHtml,
        this.htmlCallback,
        this.withActionBtn,
        this.actionList,
        this.cell,
        this.columnStyle,
        this.shouldRender
    );
  }
}

export const sortTypes = {
  DATE: "date",
  NUMERIC: "numeric",
  TEXT: "text",
};
